// frontend/admin-portal/src/components/products/ExportProductsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Alert, Spinner } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

const PRESETS = [
  {
    id: "basic",
    label: "Basic",
    description: "SKU, name, category, brand, price, costs, stock",
    columns: [
      "sku",
      "name",
      "category",
      "brand",
      "price",
      "costPrice",
      "volumeCbm",
      "totalCost",
      "stock",
      "minStock",
      "unit",
      "isActive",
    ],
    includeAttributes: false,
  },
  {
    id: "full",
    label: "Full catalog",
    description: "All core fields + all specification attributes + images",
    columns: null, // means "server default + attributes"
    includeAttributes: true,
  },
  {
    id: "reorder",
    label: "Reorder sheet",
    description: "SKU, name, stock, cost, min stock (for suppliers)",
    columns: [
      "sku",
      "name",
      "brand",
      "stock",
      "minStock",
      "costPrice",
      "totalCost",
      "volumeCbm",
      "unit",
    ],
    includeAttributes: false,
  },
  {
    id: "with-images",
    label: "Catalog + Images",
    description: "Basic fields + image URLs (for website import)",
    columns: [
      "sku",
      "name",
      "category",
      "brand",
      "price",
      "costPrice",
      "volumeCbm",
      "totalCost",
      "stock",
      "description",
      "images",
      "thumbnail",
    ],
    includeAttributes: false,
  },
  {
    id: "custom",
    label: "Custom",
    description: "Pick exactly which columns to include",
    columns: null,
    includeAttributes: false,
  },
];

export default function ExportProductsModal({
  open,
  onClose,
  filters = {}, // the active filters from the Products page
  filteredCount = 0, // how many rows match the current filters
}) {
  const [columns, setColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [selected, setSelected] = useState([]);
  const [preset, setPreset] = useState("full");
  const [includeAttributes, setIncludeAttributes] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Load the column catalog once
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadingColumns(true);
    productService
      .listExportColumns()
      .then((cols) => {
        if (!alive) return;
        setColumns(cols);
        // Apply the default preset's columns
        const full = cols.map((c) => c.key);
        setSelected(full);
      })
      .catch(
        (err) => alive && setError(err.response?.data?.message || err.message),
      )
      .finally(() => alive && setLoadingColumns(false));
    return () => {
      alive = false;
    };
  }, [open]);

  // When preset changes, sync selected + includeAttributes
  useEffect(() => {
    if (preset === "custom") return;
    const p = PRESETS.find((x) => x.id === preset);
    if (!p) return;
    setIncludeAttributes(p.includeAttributes);
    if (p.columns === null) {
      // "full" preset — select all
      setSelected(columns.map((c) => c.key));
    } else {
      setSelected(p.columns.filter((k) => columns.some((c) => c.key === k)));
    }
  }, [preset, columns]);

  const toggleColumn = (key) => {
    setPreset("custom");
    setSelected((s) =>
      s.includes(key) ? s.filter((k) => k !== key) : [...s, key],
    );
  };

  const selectAll = () => {
    setPreset("custom");
    setSelected(columns.map((c) => c.key));
  };

  const clearAll = () => {
    setPreset("custom");
    setSelected([]);
  };

  // Filter down to only what we'll send to the server
  const customColumns = useMemo(() => {
    if (preset === "custom") return selected;
    return null; // let the server use its default when we're on a preset
  }, [preset, selected]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const params = {
        ...filters,
        // strip page/limit — export has its own limit
        page: undefined,
        limit: undefined,
        includeAttributes: includeAttributes ? "true" : "false",
      };
      if (customColumns && customColumns.length > 0) {
        params.columns = customColumns.join(",");
      }
      await productService.exportProducts(params);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const coreColumns = columns.filter((c) => !c.isAttribute);
  const attrColumns = columns.filter((c) => c.isAttribute);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export Products"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            loading={exporting}
            disabled={selected.length === 0 || loadingColumns}
          >
            Download CSV ({selected.length} columns)
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {filteredCount > 0 && (
        <Alert variant="info" className="mb-3">
          Exporting <strong>{filteredCount}</strong> rows matching the current
          filters. Up to 50,000 rows per export.
        </Alert>
      )}

      {/* Presets */}
      <div className="mb-3">
        <label className="fh-input-label">Preset</label>
        <div className="fh-export-presets">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fh-export-preset ${preset === p.id ? "is-active" : ""}`}
              onClick={() => setPreset(p.id)}
            >
              <div className="fh-export-preset__label">{p.label}</div>
              <div className="fh-export-preset__desc">{p.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Column picker (custom) */}
      {preset === "custom" && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="fh-input-label mb-0">
              Columns ({selected.length} of {columns.length})
            </label>
            <div className="d-flex gap-2">
              <Button size="sm" variant="ghost" onClick={selectAll}>
                Select all
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </div>

          {loadingColumns ? (
            <div className="fh-settings-loading">
              <Spinner size="md" />
            </div>
          ) : (
            <div className="fh-export-columns">
              <div className="fh-export-columns__section">
                <div className="fh-export-columns__title">Core fields</div>
                <div className="fh-export-columns__grid">
                  {coreColumns.map((c) => (
                    <label key={c.key} className="fh-checkbox">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.key)}
                        onChange={() => toggleColumn(c.key)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {attrColumns.length > 0 && (
                <div className="fh-export-columns__section">
                  <div className="fh-export-columns__title">
                    Specification attributes ({attrColumns.length})
                  </div>
                  <div className="fh-export-columns__grid">
                    {attrColumns.map((c) => (
                      <label key={c.key} className="fh-checkbox">
                        <input
                          type="checkbox"
                          checked={selected.includes(c.key)}
                          onChange={() => toggleColumn(c.key)}
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Full preset — just show that attributes are included */}
      {preset === "full" && (
        <Alert variant="info">
          Every core field plus every specification attribute used by your
          products will be included.
        </Alert>
      )}

      {/* Non-custom presets still let you disable attributes for full */}
      {preset === "full" && (
        <label className="fh-checkbox mt-2">
          <input
            type="checkbox"
            checked={includeAttributes}
            onChange={(e) => setIncludeAttributes(e.target.checked)}
          />
          <span>Include specification attributes</span>
        </label>
      )}
    </Modal>
  );
}
